import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { events } from "@/mocks/events";
import { statusMeta } from "@/mocks/site";
import { siteConfig } from "@/mocks/site";
import EventCard from "@/components/feature/EventCard";
import type { GuaEvent } from "@/types";

function NotFoundContent() {
  return (
    <section className="mx-auto max-w-3xl px-4 py-24 text-center">
      <i className="ri-fridge-line text-6xl text-foreground-300" />
      <h1 className="mt-6 font-display text-3xl font-black text-foreground-950">这个瓜好像没找到</h1>
      <p className="mt-3 text-foreground-600">链接可能失效了，回到本期瓜单重新开吃吧。</p>
      <Link
        to="/#gua"
        className="mt-6 inline-flex whitespace-nowrap items-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600 cursor-pointer"
      >
        返回本期瓜单
      </Link>
    </section>
  );
}

export default function GuaDetailPage() {
  const { id } = useParams();
  const [copied, setCopied] = useState(false);
  const event = events.find((e) => e.id === id);

  if (!event) return <NotFoundContent />;
  const st = statusMeta[event.status] || statusMeta.reviewing;
  const related = events.filter((e) => e.catId === event.catId && e.id !== event.id).slice(0, 3);
  const related2 = related.length >= 3 ? related : [...related, ...events.filter((e) => e.id !== event.id && !related.some((r) => r.id === e.id)).slice(0, 3 - related.length)];

  const copyLink = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href).then(() => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  return (
    <>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: event.title,
          description: event.summary,
          image: [event.cover],
          articleSection: event.catName,
          datePublished: "2026-09-06",
          dateModified: `2026-${event.updated}`,
          author: { "@type": "Organization", name: siteConfig.name },
          publisher: { "@type": "Organization", name: siteConfig.name, url: siteConfig.siteUrl },
          mainEntityOfPage: { "@type": "WebPage", "@id": `${siteConfig.siteUrl}/gua/${event.id}` },
          inLanguage: "zh-CN",
          breadcrumb: {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: `${siteConfig.siteUrl}/` },
              { "@type": "ListItem", position: 2, name: "本期瓜单", item: `${siteConfig.siteUrl}/#gua` },
              { "@type": "ListItem", position: 3, name: event.title },
            ],
          },
        })}
      </script>

      <section className="mx-auto max-w-7xl px-4 md:px-6 py-10">
        {/* breadcrumb */}
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
          <Link to="/" className="hover:text-primary-700 cursor-pointer">首页</Link>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          <Link to="/#gua" className="hover:text-primary-700 cursor-pointer">本期瓜单</Link>
          <i className="ri-arrow-right-s-line text-foreground-300" />
          <span className="text-foreground-700 truncate">{event.catName}</span>
        </nav>

        {/* head */}
        <header className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${st.chip}`}>
                <i className={`${st.icon} text-[13px]`} />
                {st.label}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-background-100 px-3 py-1 text-xs font-medium text-foreground-700">
                <i className="ri-folder-line text-accent-600" />
                {event.catName}
              </span>
              <span className="inline-flex items-center gap-1 text-xs text-foreground-500">
                <i className="ri-calendar-line" />
                更新于 2026-{event.updated}
              </span>
            </div>
            <h1 className="mt-4 font-display text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
              {event.title}
            </h1>
            <p className="mt-4 text-base leading-relaxed text-foreground-700 md:text-lg">{event.summary}</p>

            <div className="mt-5 flex flex-wrap gap-1.5">
              {event.tags.map((t) => (
                <span key={t} className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900">
                  #{t}
                </span>
              ))}
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-dashed border-background-200 py-3 text-sm text-foreground-600">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-fire-fill text-primary-500" />
                热度 <b className="text-foreground-900">{event.heat}</b>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-user-3-line text-accent-600" />
                <b className="text-foreground-900">{event.crowd}</b> 人围在场内
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-list-check-3 text-secondary-600" />
                <b className="text-foreground-900">{event.timeline.length}</b> 个关键节点
              </span>
              <button
                type="button"
                onClick={copyLink}
                className="inline-flex whitespace-nowrap items-center gap-1 rounded-md px-2 py-1 text-sm text-primary-700 hover:bg-primary-50 cursor-pointer"
              >
                <i className={`${copied ? "ri-check-line" : "ri-link"}`} />
                {copied ? "链接已复制" : "复制链接"}
              </button>
            </div>
          </div>

          {/* hero cover */}
          <div className="lg:col-span-4">
            <figure className="overflow-hidden rounded-2xl border border-background-200 shadow-soft">
              <img src={event.cover} alt={event.title} className="aspect-[4/3] w-full object-cover" />
            </figure>
          </div>
        </header>

        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12">
          {/* main body */}
          <div className="lg:col-span-8">
            {/* editor's note */}
            <section className="rounded-2xl border-l-4 border-primary-500 bg-primary-50 p-5">
              <p className="text-xs font-semibold uppercase tracking-widest text-primary-700">编辑导读</p>
              <p className="mt-2 text-[15px] leading-relaxed text-foreground-800">{event.lead}</p>
            </section>

            {/* timeline */}
            <section className="mt-10">
              <h2 className="flex items-center gap-2 font-display text-xl font-black text-foreground-950">
                <i className="ri-time-line text-accent-600" />
                事件时间线
              </h2>
              <ol className="mt-6 space-y-0">
                {event.timeline.map((t, i) => (
                  <li key={t.title} className="relative flex gap-5 pb-8 last:pb-0">
                    <div className="flex flex-col items-center">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-primary-200 bg-background-50 font-display text-sm font-black text-primary-600">
                        {i + 1}
                      </span>
                      {i < event.timeline.length - 1 && <span className="mt-1 w-px flex-1 bg-background-200" />}
                    </div>
                    <div className="min-w-0 flex-1 pt-1">
                      <p className="text-xs font-semibold uppercase tracking-wider text-accent-700">{t.time}</p>
                      <h3 className="mt-1 text-base font-bold text-foreground-950">{t.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{t.desc}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            {/* clues */}
            <section className="mt-10">
              <h2 className="flex items-center gap-2 font-display text-xl font-black text-foreground-950">
                <i className="ri-search-line text-accent-600" />
                必看线索
              </h2>
              <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-3">
                {event.clues.map((c) => (
                  <div key={c.label} className="rounded-xl border border-background-200 bg-background-100/50 p-4">
                    <div className="flex items-center gap-2">
                      <i className="ri-focus-3-line text-primary-600" />
                      <h3 className="text-sm font-bold text-foreground-900">{c.label}</h3>
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-foreground-600">{c.note}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* stances */}
            <section className="mt-10">
              <h2 className="flex items-center gap-2 font-display text-xl font-black text-foreground-950">
                <i className="ri-bar-chart-grouped-line text-accent-600" />
                各方分歧
              </h2>
              <div className="mt-5 space-y-3">
                {event.stances.map((s) => (
                  <div key={s.side} className="rounded-xl border border-background-200 bg-background-50 p-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold text-foreground-900">{s.side}</span>
                      <span className="text-xs text-foreground-500">{s.ratio}% 立场占比</span>
                    </div>
                    <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-background-200">
                      <div className="h-full rounded-full bg-gradient-to-r from-accent-400 to-accent-600" style={{ width: `${s.ratio}%` }} />
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{s.point}</p>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-foreground-400">* 立场占比基于公开讨论氛围估算，仅供参考。</p>
            </section>
          </div>

          {/* aside */}
          <aside className="lg:col-span-4 space-y-5" aria-label="侧栏">
            {/* quick overview */}
            <div className="rounded-2xl border border-background-200 bg-background-50 p-5">
              <h3 className="font-display text-base font-bold text-foreground-950">热度速览</h3>
              <div className="mt-4 flex items-center justify-between rounded-xl bg-background-100 px-4 py-3">
                <span className="text-sm text-foreground-600">热度指数</span>
                <span className="font-display text-2xl font-black text-primary-600">{event.heat}</span>
              </div>
              <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-background-200">
                <div className="h-full rounded-full bg-gradient-to-r from-primary-400 to-primary-600" style={{ width: `${event.heat}%` }} />
              </div>
              <ul className="mt-5 space-y-3 text-sm text-foreground-600">
                <li className="flex items-center justify-between">
                  <span>专区</span><b className="text-foreground-900">{event.catName}</b>
                </li>
                <li className="flex items-center justify-between">
                  <span>在场人数</span><b className="text-foreground-900">{event.crowd}</b>
                </li>
                <li className="flex items-center justify-between">
                  <span>更新</span><b className="text-foreground-900">2026-{event.updated}</b>
                </li>
              </ul>
            </div>

            {/* how to read */}
            <div className="rounded-2xl bg-foreground-950 p-5 text-background-50">
              <div className="flex items-center gap-2">
                <i className="ri-book-open-line text-primary-400" />
                <h3 className="font-display text-base font-bold">吃不懂这个瓜？</h3>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-background-200">
                从时间线补起，再对照必看线索逐条核，记得去指南专栏补一下辨别真假的方法。
              </p>
              <Link to="/zhinan/g2" className="mt-4 inline-flex whitespace-nowrap items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-primary-600 cursor-pointer">
                学打假清单
                <i className="ri-arrow-right-line" />
              </Link>
            </div>

            {/* related gua */}
            <div className="rounded-2xl border border-background-200 bg-background-50 p-5">
              <h3 className="font-display text-base font-bold text-foreground-950">同片瓜田</h3>
              <ul className="mt-3 space-y-3">
                {related2.map((r) => (
                  <li key={r.id}>
                    <Link to={`/gua/${r.id}`} className="group flex gap-3 cursor-pointer">
                      <img src={r.cover} alt={r.title} className="h-14 w-20 shrink-0 rounded-md object-cover" />
                      <div className="min-w-0">
                        <p className="line-clamp-2 text-sm font-medium text-foreground-800 transition-colors group-hover:text-primary-700">{r.title}</p>
                        <p className="mt-1 text-xs text-foreground-500">{r.catName} · 热度 {r.heat}</p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>

        {/* related full cards */}
        <section className="mt-14 border-t border-background-200 pt-10">
          <div className="flex items-end justify-between">
            <h2 className="font-display text-2xl font-black text-foreground-950">相关瓜单</h2>
            <Link to="/#gua" className="inline-flex whitespace-nowrap items-center gap-1 text-sm font-semibold text-primary-700 hover:text-primary-800 cursor-pointer">
              更多本期瓜
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related2.map((r: GuaEvent) => (
              <EventCard key={r.id} event={r} />
            ))}
          </div>
        </section>
      </section>
    </>
  );
}